import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CandlestickSeries,
  createChart,
  CrosshairMode,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle, Gap } from '@pipyar/core';
import { chartPalette } from './chartTheme';
import { GapPrimitive } from './gapPrimitive';
import { fitWithShift, showLatest } from './chartShift';
import { cn } from '@/lib/utils';
import { useLiveBar } from '@/lib/useLiveBar';

interface CandleChartProps {
  candles: Candle[];
  gaps?: Gap[];
  /**
   * The instrument's stored-clock offset, for dating gaps.
   *
   * Gap colour is by age, and "today" has to mean today on the broker clock —
   * the one the axis shows — not on the viewer's. Omitted, the ages are read
   * on UTC, which is only wrong for the hours either side of midnight.
   */
  storedGmtOffsetSec?: number;
  digits: number;
  theme: 'dark' | 'light';
  /** Fit the visible range to the data whenever the series identity changes. */
  resetKey?: string;
  /**
   * Bars visible on open, instead of fitting the whole series.
   *
   * Needed once a series is long enough that fitting it is useless: the gaps
   * module loads every M1 bar it holds — well over a hundred thousand — and
   * fitContent would draw each of them a fraction of a pixel wide. The history
   * is still all there to scroll back through; only the opening view is
   * bounded.
   */
  visibleBars?: number;
  className?: string;
  /** Shown in the hover legend. */
  symbol?: string;
  timeframe?: string;
}

/**
 * Candlestick chart with gap levels drawn as lines extending to the right.
 *
 * A gap line starts AT the bar that made it and runs right, rather than
 * spanning the full width: `createPriceLine` would be one call, but it draws
 * across the whole chart including the history before the gap existed, which
 * reads as though the level had always been there.
 *
 * The lines are painted by a single canvas primitive (gapPrimitive) rather
 * than a LineSeries each. A month of M1 history holds ~2,500 gaps once the
 * filled ones are shown, and that many series makes the chart unusable; the
 * primitive also dates each line, which is what the age colours need.
 */
export function CandleChart({
  candles,
  gaps = [],
  storedGmtOffsetSec = 0,
  digits,
  theme,
  resetKey,
  visibleBars,
  className,
  symbol,
  timeframe,
}: CandleChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // Last candle follows the pushed price between refetches.
  useLiveBar(candleSeriesRef, candles);
  const gapPrimitiveRef = useRef<GapPrimitive | null>(null);

  /** Bar time under the crosshair; null when the pointer is off the chart. */
  const [hoverTime, setHoverTime] = useState<number | null>(null);

  // Lookup by time so the legend can show tick volume, which is not part of the
  // data handed to the candlestick series and so is absent from the crosshair
  // payload.
  const byTime = useMemo(() => {
    const m = new Map<number, Candle>();
    for (const c of candles) m.set(c.time, c);
    return m;
  }, [candles]);

  // Falls back to the newest bar when nothing is hovered, which is what every
  // trading chart does — an empty legend on load reads as broken.
  const legendCandle =
    (hoverTime !== null ? byTime.get(hoverTime) : undefined) ??
    (candles.length > 0 ? candles[candles.length - 1] : undefined);

  const previousCandle = useMemo(() => {
    if (!legendCandle) return undefined;
    const i = candles.findIndex((c) => c.time === legendCandle.time);
    return i > 0 ? candles[i - 1] : undefined;
  }, [candles, legendCandle]);

  // ---- create / destroy ----
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const palette = chartPalette(theme);

    const chart = createChart(container, {
      autoSize: true,
      layout: {
        background: { color: palette.background },
        textColor: palette.text,
        attributionLogo: false,
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 6,
      },
      crosshair: {
        /*
         * Normal, not the library's default of Magnet.
         *
         * Magnet snaps the price readout to the nearest OHLC of the bar under
         * the pointer, so the crosshair jumps to an open or close instead of
         * following the mouse. That is useful for reading a candle and actively
         * wrong for measuring anything between them — judging where a knot sits
         * inside a body, or how far price is from a gap edge.
         */
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
        horzLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
      },
      localization: {
        // Bar times are already on the instrument's stored clock, so they are
        // formatted as if UTC. Applying the browser's timezone here would shift
        // every label away from the broker clock the trader is working in.
        timeFormatter: (t: Time) => formatStoredTime(t as UTCTimestamp),
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: palette.bull,
      downColor: palette.bear,
      borderUpColor: palette.bull,
      borderDownColor: palette.bear,
      wickUpColor: palette.bull,
      wickDownColor: palette.bear,
      priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    const gapPrimitive = new GapPrimitive();
    candleSeries.attachPrimitive(gapPrimitive);
    gapPrimitiveRef.current = gapPrimitive;

    /**
     * Track which bar the crosshair is over.
     *
     * Only the bar TIME goes into React state, and only when it actually
     * changes. The event fires on every pointer move, so storing the candle
     * itself would re-render the legend dozens of times per second while the
     * pointer travels across a single bar, for no visible difference.
     */
    chart.subscribeCrosshairMove((param) => {
      const t = param.time === undefined ? null : (param.time as number);
      setHoverTime((prev) => (prev === t ? prev : t));
    });

    return () => {
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      // Removing the chart detaches the primitive with it.
      gapPrimitiveRef.current = null;
    };
    // Recreating on theme change is simpler and cheap here; applyOptions would
    // need every colour re-specified across chart and series anyway.
  }, [theme, digits]);

  // ---- candle data ----
  useEffect(() => {
    const series = candleSeriesRef.current;
    if (!series) return;

    series.setData(
      candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );
  }, [candles]);

  // ---- gap lines ----
  useEffect(() => {
    const primitive = gapPrimitiveRef.current;
    if (!primitive) return;

    if (candles.length === 0 || gaps.length === 0) {
      primitive.setDrawing(null);
      return;
    }

    const palette = chartPalette(theme);
    primitive.setDrawing({
      gaps: gaps.map((g) => ({
        time: g.time,
        level: g.level,
        filled: g.filled,
        filledTime: g.filledTime,
      })),
      // The loaded window, so a gap formed before the first candle can be
      // clamped to the left edge rather than dropped.
      firstTime: candles[0]!.time,
      lastTime: candles[candles.length - 1]!.time,
      // Now on the stored clock, so "today" means the broker's day — the one
      // the axis is labelled with.
      nowStored: Date.now() / 1000 + storedGmtOffsetSec,
      colors: {
        today: palette.gapToday,
        week: palette.gapWeek,
        older: palette.gapOlder,
      },
    });
  }, [gaps, candles, theme, storedGmtOffsetSec]);

  // ---- position the view when the series changes ----
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (visibleBars === undefined || candles.length <= visibleBars) {
      fitWithShift(chart.timeScale());
      return;
    }

    showLatest(chart.timeScale(), candles.length - 1, visibleBars);
  }, [resetKey, visibleBars, candles]);

  const palette = chartPalette(theme);

  return (
    // `relative` so the legend can sit over the canvas.
    <div className={cn('relative', className)}>
      <div ref={containerRef} className="absolute inset-0" />

      {legendCandle && (
        <OhlcLegend
          candle={legendCandle}
          previous={previousCandle}
          digits={digits}
          palette={palette}
          symbol={symbol}
          timeframe={timeframe}
          live={hoverTime === null}
        />
      )}
    </div>
  );
}

interface OhlcLegendProps {
  candle: Candle;
  previous: Candle | undefined;
  digits: number;
  palette: ReturnType<typeof chartPalette>;
  symbol?: string;
  timeframe?: string;
  /** True when showing the latest bar rather than a hovered one. */
  live: boolean;
}

/**
 * OHLC readout in the chart's top-left corner.
 *
 * `pointer-events-none` throughout: the legend overlays the plot area, and
 * without it the crosshair would freeze wherever the pointer crossed the box.
 */
function OhlcLegend({
  candle,
  previous,
  digits,
  palette,
  symbol,
  timeframe,
  live,
}: OhlcLegendProps) {
  const up = candle.close >= candle.open;
  const color = up ? palette.bull : palette.bear;

  // Change is measured against the previous bar's CLOSE, not this bar's open —
  // that is the convention every trading platform uses, and on a gapping
  // instrument the two give visibly different numbers.
  const base = previous?.close;
  const change = base === undefined ? null : candle.close - base;
  const changePct = base === undefined || base === 0 ? null : ((candle.close - base) / base) * 100;

  const fmt = (n: number) => n.toFixed(digits);

  return (
    <div className="pointer-events-none absolute top-2.5 start-3 z-10 select-none">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 text-xs">
        {(symbol || timeframe) && (
          <span className="font-semibold text-foreground">
            {symbol}
            {symbol && timeframe ? ' · ' : ''}
            {timeframe}
          </span>
        )}
        <span className="tabular text-muted-foreground">{formatStoredTime(candle.time as UTCTimestamp)}</span>
        {live && (
          <span className="rounded bg-muted px-1 py-px text-[10px] font-medium text-muted-foreground">
            latest
          </span>
        )}
      </div>

      <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-xs">
        <Field label="O" value={fmt(candle.open)} color={color} />
        <Field label="H" value={fmt(candle.high)} color={color} />
        <Field label="L" value={fmt(candle.low)} color={color} />
        <Field label="C" value={fmt(candle.close)} color={color} />

        {change !== null && changePct !== null && (
          <span className="tabular font-medium" style={{ color }}>
            {change >= 0 ? '+' : ''}
            {fmt(change)} ({changePct >= 0 ? '+' : ''}
            {changePct.toFixed(2)}%)
          </span>
        )}

        <span className="tabular text-muted-foreground">
          vol {candle.tickVolume.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function Field({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <span className="tabular">
      <span className="text-muted-foreground">{label}</span>{' '}
      <span style={{ color }} className="font-medium">
        {value}
      </span>
    </span>
  );
}

/**
 * Format a stored-clock timestamp for axis and crosshair labels.
 *
 * Uses the UTC getters on purpose: the number is a wall-clock reading on the
 * broker's shifted clock, not a true instant, so local-time formatting would
 * misreport it.
 */
function formatStoredTime(t: UTCTimestamp): string {
  const d = new Date((t as number) * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(
    d.getUTCHours(),
  )}:${pad(d.getUTCMinutes())}`;
}
